import { apiClient } from '../../helper/api'

export type TaxFeeListByState = Record<string, string[]>

export function isMappedOtrTaxType(
    taxType: unknown,
    homeState: unknown,
    taxFeeList: TaxFeeListByState
): boolean {
    const value = String(taxType || '').trim()
    const key = String(homeState || '').trim()
    if (!value || !key) return false
    return (taxFeeList[key] || []).includes(value)
}

export function otrTaxTypeFromRow(row: { taxType?: string; taxName?: string } | null | undefined): string {
    return String(row?.taxType || row?.taxName || '').trim()
}

export function otrHomeStateFromRow(row: { homeState?: string; stateName?: string } | null | undefined): string {
    return String(row?.homeState || row?.stateName || '').trim()
}

export async function fetchOtrTaxNamesByStateId(stateId: number, token: string | undefined): Promise<string[]> {
    if (!stateId) return []
    const responseJson = await apiClient('/TaxesAndFees/GetAllTaxandFeeName', {
        method: 'POST',
        headers: new Headers({
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
        }),
        body: JSON.stringify({ StateId: stateId }),
    })
    return Array.isArray(responseJson?.data)
        ? responseJson.data.filter((name: unknown) => Boolean(name))
        : []
}

export function buildTaxFeeListFromFeesAndStates(
    fees: { taxName?: string; stateId?: number }[],
    states: { stateId?: number; postalAbbv?: string }[]
): TaxFeeListByState {
    const list: TaxFeeListByState = {}
    const postalByStateId = (states || []).reduce((map: Record<number, string>, state) => {
        if (state.stateId && state.postalAbbv) {
            map[state.stateId] = state.postalAbbv
        }
        return map
    }, {})
    for (const fee of fees || []) {
        if (!fee.taxName) continue
        const key = postalByStateId[Number(fee.stateId)]
        if (!key) continue
        list[key] = list[key] || []
        if (!list[key].includes(fee.taxName)) {
            list[key].push(fee.taxName)
        }
    }
    return list
}

export function getOtrApproveFieldIssues(
    item: { transactionType?: string; taxType?: string; taxName?: string; homeState?: string; stateName?: string },
    taxFeeList: TaxFeeListByState,
    hasValidTransactionType: boolean
): { invalidTransactionType: boolean; invalidTaxType: boolean } {
    return {
        invalidTransactionType: !hasValidTransactionType,
        invalidTaxType: !isMappedOtrTaxType(otrTaxTypeFromRow(item), otrHomeStateFromRow(item), taxFeeList),
    }
}

export function summarizeOtrApproveFieldIssues(
    items: { transactionType?: string; taxType?: string; taxName?: string; homeState?: string; stateName?: string }[],
    taxFeeList: TaxFeeListByState,
    hasValidTransactionType: (item: { transactionType?: string }) => boolean
): { invalidTransactionType: boolean; invalidTaxType: boolean } {
    let invalidTransactionType = false
    let invalidTaxType = false
    for (const item of items || []) {
        const issues = getOtrApproveFieldIssues(item, taxFeeList, hasValidTransactionType(item))
        invalidTransactionType = invalidTransactionType || issues.invalidTransactionType
        invalidTaxType = invalidTaxType || issues.invalidTaxType
    }
    return { invalidTransactionType, invalidTaxType }
}

export function buildOtrApproveSkipLabel(issues: { invalidTransactionType: boolean; invalidTaxType: boolean }): string {
    if (issues.invalidTransactionType && issues.invalidTaxType) {
        return 'Transaction Type or Tax Type'
    }
    if (issues.invalidTaxType) {
        return 'Tax Type'
    }
    return 'Transaction Type'
}

export function buildOtrApproveBlockedMessage(
    count: number,
    issues: { invalidTransactionType: boolean; invalidTaxType: boolean },
    transactionTypeHelp?: string
): string {
    const fieldLabel = buildOtrApproveSkipLabel(issues)
    const lines = [
        `<p>All ${count} selected item(s) have an invalid ${fieldLabel}.</p>`,
        '<p>Please update those required fields before approving.</p>',
    ]
    if (issues.invalidTransactionType && transactionTypeHelp) {
        lines.push(`<p><strong>Transaction Type:</strong> ${transactionTypeHelp}</p>`)
    }
    if (issues.invalidTaxType) {
        lines.push('<p><strong>Tax Type:</strong> Select a mapped tax type for the home state.</p>')
    }
    return lines.join('')
}
